import { Injectable } from '@angular/core';
import { AbilityBuilder, AbilityClass, createMongoAbility, MongoAbility } from '@casl/ability';

// Mirror backend subjects
export const SUBJECTS = [
  'Organization',
  'PartnerOrganization',
  'Cluster',
  'Operation',
  'HumanResource',
  'Vehicle',
  'Location',
  'Document',
  'JobFile',
  'Tender',
  'Bid',
  'ProcurementContract',
  'SalesContract',
  'Pow',
  'Invoice',
  'ServiceCatalog',
  'FlightSchedule',
  'VesselSchedule',
  'ServiceDelivery',
  'Role',
  'Activity',
  'WorkOrder',
  'EventFeedEvent',
  'Control-Tower',
  'Collaborators',
  'Task-Feed',
  'Events-Feed',
  'Map',
  'Dsr',
  'Tip',
  'War-Room',
  'Job-Activity',
  'Key-Dates',
  'General-Details',
  'Tip-Key-Dates',
  'Cargo-Details',
  'Document-Vault',
  'Document-Vault-Proforma-Invoice',
  'Document-Vault-Packing-List',
  'Document-Vault-Msds',
  'Document-Vault-Commercial-Invoice',
  'Document-Vault-Vessel-Booking',
  'Document-Vault-Letter-Of-Credit',
  'Document-Vault-Certificate-Of-Origin',
  'Document-Vault-Freight-Invoice',
  'Document-Vault-Customs-Declaration',
  'Document-Vault-Email-Confirmations',
  'Document-Vault-Phytosanitary-Certificate',
  'Document-Vault-Arrival-Notice',
  'Document-Vault-Goods-Received-Notes',
  'Document-Vault-Service-Delivery',
  'Document-Vault-Main-Contract',
  'Document-Vault-Supporting-Contracts',
  'Document-Vault-Bills-Of-Exchange-Or-Drafts',
  'Document-Vault-Consignment-Agreement',
  'Document-Vault-Escrow-Agreement',
  'Document-Vault-Purchase-Order',
  'Document-Vault-Flight-Booking',
  'Document-Vault-Bill-Of-Lading',
  'Document-Vault-Draft-Bill-Of-Lading',
  'Document-Vault-Air-Waybill',
  'Document-Vault-Other-Documents',
] as const;

export type Subjects = (typeof SUBJECTS)[number];
export type Actions = string;

// Dynamic actions from scopes
export type AppAbility = MongoAbility<[Actions, Subjects | Record<string, unknown>]>;

@Injectable({ providedIn: 'root' })
export class AbilityFactory {
  createAbility(scopes: string[] = []): AppAbility {
    const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility as unknown as AbilityClass<AppAbility>);

    const granularPermissions = new Map<string, Set<string>>();

    for (const scope of scopes) {
      const scopeParts = scope.split(':');
      if (scopeParts.length === 3) {
        const [serviceId, subjectKey, action] = scopeParts;
        const subject = SUBJECTS.find(s => s.toLowerCase() === subjectKey.toLowerCase());
        if (subject) {
          if (!granularPermissions.has(subject)) {
            granularPermissions.set(subject, new Set());
          }
          granularPermissions.get(subject)?.add(action);
          can(action, subject, { serviceId });
        }
      }
    }

    for (const scope of scopes) {
      const scopeParts = scope.split(':');
      if (scopeParts.length === 2) {
        const [subjectKey, action] = scopeParts;
        const subject = SUBJECTS.find(s => s.toLowerCase() === subjectKey.toLowerCase());

        if (subject) {
          if (action === 'any') {
            if (!granularPermissions.has(subject)) {
              can('manage', subject);
            }
          } else {
            if (!granularPermissions.has(subject) || !granularPermissions.get(subject)?.has(action)) {
              can(action, subject);
            }
          }
        }
      }
    }

    const ability = build();
    return ability;
  }
}
